/**
 * `/jobs/:jobId/stop` — the stop-schedule deep-link landing (FR-SCHED-3).
 * Every scheduled-run email/SMS carries this link in its MESSAGE body; the
 * page confirms before disabling (turning the schedule OFF preserves every
 * other setting — FR-SCHED-5 — so it can be re-enabled from the console).
 * Auth-gated: the schedules API is sub-scoped, so a forwarded link can
 * never stop someone else's schedule (a foreign jobId just 404s).
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  describeSchedule,
  disableSchedule,
  getSchedule,
  type JobDefinition,
} from '../../agents/jobsApi.ts';
import './StopSchedule.css';

type Phase = 'loading' | 'confirm' | 'stopping' | 'stopped' | 'missing' | 'error';

export default function StopSchedule() {
  const { jobId } = useParams<{ jobId: string }>();
  const [phase, setPhase] = useState<Phase>('loading');
  const [def, setDef] = useState<JobDefinition | null>(null);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    const cancelled = { current: false };
    const isCancelled = (): boolean => cancelled.current;
    void (async () => {
      if (jobId === undefined || jobId.length === 0) {
        setPhase('missing');
        return;
      }
      try {
        const loaded = await getSchedule(jobId);
        if (!isCancelled()) {
          setDef(loaded);
          setPhase(loaded.enabled ? 'confirm' : 'stopped');
        }
      } catch {
        // Sub-scoped 404 and transport errors land here alike; the page
        // only needs "can't act on this schedule" fidelity.
        if (!isCancelled()) {
          setPhase('missing');
        }
      }
    })();
    return () => {
      cancelled.current = true;
    };
  }, [jobId]);

  async function stop() {
    if (jobId === undefined) {
      return;
    }
    setPhase('stopping');
    try {
      await disableSchedule(jobId);
      setPhase('stopped');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not stop that schedule.');
      setPhase('error');
    }
  }

  return (
    <main className="om-stop">
      <h1 className="om-stop__title">Stop schedule</h1>

      {phase === 'loading' ? <p className="om-stop__muted">Loading the schedule…</p> : null}

      {phase === 'missing' ? (
        <>
          <p>This schedule doesn’t exist anymore (or belongs to a different account).</p>
          <Link className="om-stop__link" to="/app">
            Back to your console
          </Link>
        </>
      ) : null}

      {(phase === 'confirm' || phase === 'stopping') && def !== null ? (
        <>
          <p>
            Turn off <strong>{def.jobId}</strong>? It runs the workflow{' '}
            <code>{def.workflowId}</code> {describeSchedule(def)}
            {def.params !== undefined
              ? ` with ${Object.entries(def.params)
                  .map(([k, v]) => `${k}=${v}`)
                  .join(', ')}`
              : ''}
            .
          </p>
          <p className="om-stop__muted">
            Stopping only turns the schedule off — its settings are kept, and you can turn it
            back on from the console’s Jobs panel.
          </p>
          <button
            type="button"
            className="om-stop__cta"
            disabled={phase === 'stopping'}
            onClick={() => {
              void stop();
            }}
          >
            {phase === 'stopping' ? 'Stopping…' : 'Stop this schedule'}
          </button>
        </>
      ) : null}

      {phase === 'stopped' ? (
        <>
          <p>
            <strong>{def?.jobId ?? jobId}</strong> is off. Nothing more will run on this
            schedule.
          </p>
          <Link className="om-stop__link" to="/app">
            Back to your console
          </Link>
        </>
      ) : null}

      {phase === 'error' ? (
        <>
          <p className="om-stop__error">{error}</p>
          <button
            type="button"
            className="om-stop__cta"
            onClick={() => {
              void stop();
            }}
          >
            Try again
          </button>
        </>
      ) : null}
    </main>
  );
}
